import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';

/**
 * A confirmation dialog.
 *
 * #### Usage
 *
 * ```ts
 * const dialogRef = this.dialog.open(ConfirmationDialog, {
 *    data: {
 *      title: 'MODULE.DIALOG_DELETE.TITLE',
 *      messages: ['MODULE.DIALOG_DELETE.MESSAGE1', 'MODULE.DIALOG_DELETE.MESSAGE2'],
 *      // or, if you have only one message:
 *      description: 'MODULE.DIALOG_DELETE.DESCRIPTION',
 *    } as ConfirmationDialogData,
 *  });
 * dialogRef.afterClosed().subscribe(result => {
 *   if (!!result) {
 *     // deletion was confirmed
 *     // deletion code here
 *   }
 * });
 * ```
 */
@Component({
  selector: 'cipo-confirmation-dialog',
  template: `
    <h2 mat-dialog-title data-testid="cipo-confirmation-dialog-title">{{ data.title | translate: data.params }}</h2>
    <mat-dialog-content>
      <div class="p-t" data-testid="cipo-confirmation-dialog-content">
        @if (data.description) {
          <p [innerHTML]="data.description | translate: data.params"></p>
        }
        @for (message of data.messages; track message) {
          <p [innerHTML]="message | translate: data.params"></p>
        }
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-raised-button data-testid="cipo-confirmation-dialog-no-action" [mat-dialog-close]="false">
        {{ data.noLabel ?? 'general.no' | translate }}
      </button>
      <button
        mat-raised-button
        data-testid="cipo-confirmation-dialog-yes-action"
        [mat-dialog-close]="true"
        color="accent"
        cdkFocusInitial
      >
        {{ data.yesLabel ?? 'general.yes' | translate }}
      </button>
    </mat-dialog-actions>
  `,
  imports: [MatButtonModule, MatDialogModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmationDialog {
  readonly data = inject<ConfirmationDialogData>(MAT_DIALOG_DATA);
}

export interface ConfirmationDialogData {
  title: string;
  params?: Record<string, any>;
  messages?: string[];
  description?: string;
  yesLabel?: string;
  noLabel?: string;
}
